import { Badge, Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { IconFilePlus } from "@tabler/icons-react";
import { Link } from "react-router";
import { ErrorState } from "@/components/States";
import { useClientOptions, useClientRequests } from "./api";
import { DashboardCounts } from "./DashboardCounts";
import { stageLabel, STAGE_COLOR, STAGE_HELP, STAGE_ORDER } from "./stage";

function FirstRequisition() {
  return (
    <Card p={{ base: "lg", sm: 40 }}>
      <Stack gap="lg" maw={640}>
        <Stack gap={6}>
          <Title order={2} fz={26}>
            Send your first dossier requisition
          </Title>
          <Text c="dimmed" lh={1.6}>
            Tell Makin which product and which country the registration is for,
            and what you need: a dossier checklist, guidelines or specific
            documents. Makin gives you a date once the work is planned, and you
            can download the approved documents here.
          </Text>
        </Stack>
        <Stack gap={10}>
          {STAGE_ORDER.map((s) => (
            <Group key={s} gap="sm" wrap="nowrap" align="flex-start">
              <Badge
                color={STAGE_COLOR[s]}
                variant="light"
                w={148}
                style={{ flex: "none" }}
              >
                {stageLabel(s)}
              </Badge>
              <Text fz="sm" c="dimmed">
                {STAGE_HELP[s as Exclude<typeof s, "cancelled">]}
              </Text>
            </Group>
          ))}
        </Stack>
        <Group>
          <Button
            component={Link}
            to="/requests/new"
            size="md"
            leftSection={<IconFilePlus size={18} />}
          >
            New requisition
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * The client's dashboard: their numbers as twelve tiles, each opening the list it counts. A company with no requisition
 * yet is shown how to send the first one.
 */
export function ClientDashboardPage() {
  const options = useClientOptions();
  const open = useClientRequests("open");

  if (open.isError)
    return (
      <ErrorState error={open.error} onRetry={() => void open.refetch()} />
    );
  const counts = open.data?.counts;
  const nothingYet = !open.isLoading && counts && counts.all === 0;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
        <Stack gap={4}>
          <Title
            order={1}
            fz={{ base: 26, sm: 32 }}
            style={{ letterSpacing: "-0.015em" }}
          >
            Dashboard
          </Title>
          <Text c="dimmed">
            {options.data?.company.name ?? " "}
            {counts ? ` · ${counts.open} open` : ""}
          </Text>
        </Stack>
        {!nothingYet && (
          <Button
            component={Link}
            to="/requests/new"
            leftSection={<IconFilePlus size={16} />}
          >
            New requisition
          </Button>
        )}
      </Group>

      <DashboardCounts />

      {nothingYet && <FirstRequisition />}
    </Stack>
  );
}
